import React, { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/EditRounded';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import { Flex, FlexColumn } from 'src/components/flex';
import LeadSidebarExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import CreateOrUpdatePropertyModal from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal';
import { useProperty } from 'src/services/api';
import type { IProperty } from 'src/services/api/properties/types';
import { PROPERTY_FACT_KEY_LABELS } from 'src/services/api/properties/property-fact-keys';

import styles from 'src/components/lead-sidebar/LeadSidebarPropertyTab.module.css';

function formatAddress(p: IProperty): string {
    const street = p.unit ? `${p.street_address}, Unit ${p.unit}` : p.street_address;
    return `${street}, ${p.city}, ${p.state} ${p.zipcode}`;
}

interface FactRowProps {
    label: string;
    value: string;
}

function isLink(value: string): boolean {
    return (value.startsWith('https://') || value.startsWith('http://')) && !value.includes(' ');
}

function FactRow({ label, value }: FactRowProps) {
    const link = isLink(value);
    let domain = value;
    if (link) {
        try { domain = new URL(value).hostname; } catch { /* keep value */ }
    }
    return (
        <div className={styles.factRow}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            {link ? (
                <Link href={value} target="_blank" rel="noreferrer noopener" referrerPolicy="no-referrer" variant="body2">
                    <span className={styles.linkRow}>
                        {domain} <OpenInNewOutlinedIcon className={styles.externalLinkIcon} />
                    </span>
                </Link>
            ) : (
                <Typography variant="body2">{value}</Typography>
            )}
        </div>
    );
}

function NoKnownFacts() {
    return (
        <Typography variant="body2" color="text.secondary" className={styles.noKnownFacts}>
            no known facts
        </Typography>
    );
}

interface UnitSectionProps {
    property: IProperty;
    title: string;
}

function UnitSection({ property, title }: UnitSectionProps) {
    const [isEditing, setIsEditing] = useState(false);
    const facts = property.facts ? Object.values(property.facts) : [];
    const editAction = (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }} data-testid="edit-unit-property-button">
            <EditIcon fontSize="small" />
        </IconButton>
    );
    return (
        <>
            <LeadSidebarExpandableItem title={title} defaultExpanded action={editAction}>
                <FlexColumn rowGap={1.5} pb={1}>
                    <Typography variant="body2" fontWeight={500} className={styles.sectionName}>
                        {property.name}
                    </Typography>
                    <FactRow label="Address" value={formatAddress(property)} />
                    {property.rent != null && (
                        <FactRow label="Rent" value={`$${property.rent.toLocaleString()}/mo`} />
                    )}
                    {property.showing_scheduling_link && (
                        <div className={styles.factRow}>
                            <Typography variant="body2" color="text.secondary">Schedule a tour</Typography>
                            <Link href={property.showing_scheduling_link.link} target="_blank" rel="noreferrer" variant="body2">
                                <span className={styles.linkRow}>
                                    Open link <OpenInNewOutlinedIcon className={styles.externalLinkIcon} />
                                </span>
                            </Link>
                        </div>
                    )}
                    {property.application_link && (
                        <div className={styles.factRow}>
                            <Typography variant="body2" color="text.secondary">Apply</Typography>
                            <Link href={property.application_link.link} target="_blank" rel="noreferrer" variant="body2">
                                <span className={styles.linkRow}>
                                    Open link <OpenInNewOutlinedIcon className={styles.externalLinkIcon} />
                                </span>
                            </Link>
                        </div>
                    )}
                    {facts.map((fact) => (
                        <FactRow
                            key={fact.key}
                            label={PROPERTY_FACT_KEY_LABELS[fact.key] ?? fact.key}
                            value={fact.value}
                        />
                    ))}
                    {facts.length === 0 && <NoKnownFacts />}
                </FlexColumn>
            </LeadSidebarExpandableItem>
            {isEditing && (
                <CreateOrUpdatePropertyModal
                    opened={isEditing}
                    onClose={() => setIsEditing(false)}
                    property={property}
                />
            )}
        </>
    );
}

interface CommunitySectionProps {
    community: IProperty;
}

function CommunitySection({ community }: CommunitySectionProps) {
    const [isEditing, setIsEditing] = useState(false);
    const facts = community.facts ? Object.values(community.facts) : [];
    const editAction = (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }} data-testid="edit-community-property-button">
            <EditIcon fontSize="small" />
        </IconButton>
    );
    return (
        <>
            <LeadSidebarExpandableItem title="Community Facts" defaultExpanded action={editAction}>
                <FlexColumn rowGap={1.5} pb={1}>
                    <Typography variant="body2" fontWeight={500} className={styles.sectionName}>
                        {community.name}
                    </Typography>
                    {facts.map((fact) => (
                        <FactRow
                            key={fact.key}
                            label={PROPERTY_FACT_KEY_LABELS[fact.key] ?? fact.key}
                            value={fact.value}
                        />
                    ))}
                    {facts.length === 0 && <NoKnownFacts />}
                </FlexColumn>
            </LeadSidebarExpandableItem>
            {isEditing && (
                <CreateOrUpdatePropertyModal
                    opened={isEditing}
                    onClose={() => setIsEditing(false)}
                    property={community}
                />
            )}
        </>
    );
}

type Props = {
    leadPropertyId: number | null;
};

export default function LeadSidebarPropertyTab({ leadPropertyId }: Props) {
    const { data: property, isLoading } = useProperty(leadPropertyId, {
        include_ancestors: true,
        include_facts: true,
    });

    if (!leadPropertyId) {
        return (
            <Typography variant="body2" color="text.secondary" p={2}>
                This lead is not associated with a property.
            </Typography>
        );
    }

    if (isLoading) {
        return (
            <Flex justifyContent="center" py={2}>
                <CircularProgress size={20} />
            </Flex>
        );
    }

    if (!property) { return null; }

    const unitTitle = property.parent ? 'Unit Facts' : 'Property Facts';

    return (
        <>
            <UnitSection property={property} title={unitTitle} />
            {property.parent && <CommunitySection community={property.parent} />}
        </>
    );
}
